import { createFeatureSelector, createSelector } from '@ngrx/store';
import { isEqual } from 'lodash';
import * as moment from 'moment';

import { ComparisonMode, SortOrder } from '@app/services/summary.service';
import {
  FilterByFieldInfoModel,
  PeriodType,
  SummaryReportSettingsModel,
  SummaryType,
} from '@main-application/shared/services/summary-report-settings.service';

import { SummaryReportSettingsState } from '../reducers/summary-report-settings.reducers';

export const selectSummaryReportSettingsState =
  createFeatureSelector<SummaryReportSettingsState>('summaryReportSettings');

export const selectSummarySettingsMap = createSelector(
  selectSummaryReportSettingsState,
  state => state.summarySettings
);

export const selectCombinedSummarySetting = (type: SummaryType) =>
  createSelector(selectSummaryReportSettingsState, state => state.summarySettings?.[type] ?? null);

export const selectInitialSummarySetting = (type: SummaryType) =>
  createSelector(selectSummarySettingsMap, settings => settings[type]?.initial);

export const selectModifiedSummarySetting = (type: SummaryType) =>
  createSelector(selectSummarySettingsMap, settings => settings[type]?.modified);

export const selectSummarySettingDirty = (type: SummaryType) =>
  createSelector(selectSummarySettingsMap, settings => {
    const s: { initial: SummaryReportSettingsModel; modified: SummaryReportSettingsModel } = settings[type];
    if (!s?.initial || !s?.modified) return false;

    const { initial, modified } = s;

    const shouldCompareDates = initial.periodType === PeriodType.Custom || modified.periodType === PeriodType.Custom;
    const shouldCompareBDates = initial.periodBType === PeriodType.Custom || modified.periodBType === PeriodType.Custom;

    const startDate =
      (initial.startDate as unknown) instanceof Date
        ? moment(initial.startDate).format('YYYY-MM-DD')
        : initial.startDate;
    const endDate =
      (initial.endDate as unknown) instanceof Date ? moment(initial.endDate).format('YYYY-MM-DD') : initial.endDate;
    const modifiedStartDate =
      (modified.startDate as unknown) instanceof Date
        ? moment(modified.startDate).format('YYYY-MM-DD')
        : modified.startDate;
    const modifiedEndDate =
      (modified.endDate as unknown) instanceof Date ? moment(modified.endDate).format('YYYY-MM-DD') : modified.endDate;

    const startBDate =
      (initial.periodBStartDate as unknown) instanceof Date
        ? moment(initial.periodBStartDate).format('YYYY-MM-DD')
        : initial.periodBStartDate ?? null;
    const endBDate =
      (initial.periodBEndDate as unknown) instanceof Date
        ? moment(initial.periodBEndDate).format('YYYY-MM-DD')
        : initial.periodBEndDate ?? null;
    const modifiedStartBDate =
      (modified.periodBStartDate as unknown) instanceof Date
        ? moment(modified.periodBStartDate).format('YYYY-MM-DD')
        : modified.periodBStartDate ?? null;
    const modifiedEndBDate =
      (modified.periodBEndDate as unknown) instanceof Date
        ? moment(modified.periodBEndDate).format('YYYY-MM-DD')
        : modified.periodBEndDate ?? null;

    const result =
      initial.name !== modified.name ||
      initial.description !== modified.description ||
      initial.summaryType !== modified.summaryType ||
      initial.dataSource !== modified.dataSource ||
      initial.dataSourceModifier !== modified.dataSourceModifier ||
      (initial.reportLayout ?? 1) !== (modified.reportLayout ?? 1) ||
      (initial.vsModeEnabled ?? false) !== (modified.vsModeEnabled ?? false) ||
      (initial.vsModeComparisonMode ?? ComparisonMode.Value) !==
        (modified.vsModeComparisonMode ?? ComparisonMode.Value) ||
      initial.periodType !== modified.periodType ||
      initial.periodDateSearchFields !== modified.periodDateSearchFields ||
      (initial.periodBType !== modified.periodBType &&
        !(initial.periodBType == null && modified.periodBType == null)) ||
      initial.reportView !== modified.reportView ||
      !isEqual(initial.sortBy, modified.sortBy) ||
      (shouldCompareDates && (startDate !== modifiedStartDate || endDate !== modifiedEndDate)) ||
      !isEqual(
        initial.showBy,
        modified.showBy?.map(s => ({
          fieldType: s.fieldType,
          isDescending: s.isDescending || s.sortOrder === SortOrder.Descending,
        }))
      ) ||
      (shouldCompareBDates &&
        ((startBDate !== modifiedStartBDate && !(startBDate == null && modifiedStartBDate == null)) ||
          (endBDate !== modifiedEndBDate && !(endBDate == null && modifiedEndBDate == null)))) ||
      !areFilterByArraysEqual(initial.filterBy, modified.filterBy) ||
      !isEqual(initial.rolesCanView, modified.rolesCanView) ||
      !isEqual(initial.rolesCanEdit, modified.rolesCanEdit);

    return result;
  });

export const selectAllSummaryReportSettings = createSelector(selectSummaryReportSettingsState, state => state.settings);

export const selectSummaryReportSettingsLoading = createSelector(
  selectSummaryReportSettingsState,
  state => state.loading
);

export const selectSummaryReportSettingsError = createSelector(selectSummaryReportSettingsState, state => state.error);

export const selectIsDownloadingReport = createSelector(selectSummaryReportSettingsState, state => state.downloading);

function areFilterByArraysEqual(
  a: FilterByFieldInfoModel[] | undefined,
  b: FilterByFieldInfoModel[] | undefined
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;

  return a.every((itemA, index) => {
    const itemB = b[index];
    if (!itemB) return false;

    return (
      itemA.fieldType === itemB.fieldType &&
      itemA.values === itemB.values &&
      itemA.isSingleChoiceSelector === itemB.isSingleChoiceSelector &&
      itemA.labels === itemB.labels
    );
  });
}
